import { useState, useEffect, useRef } from "react";
import { adminFetch } from "../../lib/api";

type SortBy = "newest" | "oldest" | "address";
type ShowFilter = "all" | "current" | "unconfirmed";
type MethodFilter = "all" | "email" | "phone" | "slack" | "msteams" | "webhook";

interface SubscriberService {
  id: string;
  service_name: string;
  region_name: string;
}

interface Subscriber {
  id: string;
  email: string;
  method: string;
  contact_value: string;
  notify_all: boolean;
  min_notifications: boolean;
  confirmed: boolean;
  services: SubscriberService[];
  created_at: string;
}

interface ServiceRegionOption {
  id: string;
  service_name: string;
  region_name: string;
  component_name?: string | null;
}

function MethodBadge({ method }: { method: string }) {
  return (
    <span className="ac-badge ac-badge--method">
      {method.charAt(0).toUpperCase() + method.slice(1)}
    </span>
  );
}

function ServiceBadge({ label }: { label: string }) {
  return <span className="ac-badge ac-badge--service">{label}</span>;
}

export function SubscribersPage() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [showFilter, setShowFilter] = useState<ShowFilter>("current");
  const [methodFilter, setMethodFilter] = useState<MethodFilter>("all");
  const [loading, setLoading] = useState(true);

  // Add subscriber dialog
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fMethod, setFMethod] = useState("email");
  const [fContact, setFContact] = useState("");
  const [fNotifyAll, setFNotifyAll] = useState(true);
  const [fServiceRegionIds, setFServiceRegionIds] = useState<string[]>([]);
  const [serviceRegions, setServiceRegions] = useState<ServiceRegionOption[]>([]);

  // Kebab menu
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  async function loadSubscribers() {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    params.set("sort", sortBy);
    params.set("show", showFilter);
    if (methodFilter !== "all") params.set("method", methodFilter);

    const r = await adminFetch(`/api/v1/admin/subscribers/?${params}`);
    if (r.ok) setSubscribers(await r.json());
    setLoading(false);
  }

  async function loadServiceRegions() {
    const r = await adminFetch("/api/v1/admin/service-regions/");
    if (r.ok) {
      const data = await r.json();
      setServiceRegions(
        data.map((sr: { id: string; service_name: string; region_name: string; component_name?: string | null }) => ({
          id: sr.id,
          service_name: sr.service_name,
          region_name: sr.region_name,
          component_name: sr.component_name ?? null,
        }))
      );
    }
  }

  useEffect(() => {
    loadSubscribers();
  }, [sortBy, showFilter, methodFilter]);

  useEffect(() => {
    loadServiceRegions();
  }, []);

  // Close kebab menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") loadSubscribers();
  }

  function resetForm() {
    setFMethod("email");
    setFContact("");
    setFNotifyAll(true);
    setFServiceRegionIds([]);
    setError("");
  }

  function openCreateDialog() {
    resetForm();
    setShowCreate(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!fContact.trim()) {
      setError("Contact address is required.");
      return;
    }
    setSubmitting(true);
    setError("");

    const body: Record<string, unknown> = {
      method: fMethod,
      contact_value: fContact.trim(),
      notify_all: fNotifyAll,
    };
    if (fMethod === "email") body.email = fContact.trim();
    if (!fNotifyAll) body.service_region_ids = fServiceRegionIds;

    try {
      const r = await adminFetch("/api/v1/admin/subscribers/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.contact_value?.[0] || d.detail || `HTTP ${r.status}`);
      }
      setShowCreate(false);
      resetForm();
      await loadSubscribers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create subscriber.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this subscriber? This cannot be undone.")) return;
    const r = await adminFetch(`/api/v1/admin/subscribers/${id}/`, { method: "DELETE" });
    if (r.ok) {
      setOpenMenu(null);
      await loadSubscribers();
    }
  }

  function displayAddress(sub: Subscriber): string {
    return sub.contact_value || sub.email;
  }

  function servicesLabel(sub: Subscriber): string {
    if (sub.notify_all) return "All";
    if (sub.services.length === 0) return "None";
    return sub.services.map((s) => `${s.service_name} ${s.region_name}`).join(", ");
  }

  function toggleServiceRegion(id: string) {
    setFServiceRegionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Subscribers</h1>
        </div>
        <div className="ac-page-header__actions">
          <button
            type="button"
            className="aptean-btn aptean-btn--primary"
            onClick={openCreateDialog}
          >
            Add subscribers
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ac-filter-bar" style={{ gap: "var(--aptean-space-3)" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
          <input
            type="text"
            className="aptean-input"
            placeholder="Search subscribers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
        <label className="ac-filter-label">
          Sort by:
          <select
            className="aptean-input"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            style={{ width: 130 }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="address">Address</option>
          </select>
        </label>
        <label className="ac-filter-label">
          Show:
          <select
            className="aptean-input"
            value={showFilter}
            onChange={(e) => setShowFilter(e.target.value as ShowFilter)}
            style={{ width: 140 }}
          >
            <option value="all">All</option>
            <option value="current">Current</option>
            <option value="unconfirmed">Unconfirmed</option>
          </select>
        </label>
        <label className="ac-filter-label">
          Subscribed by:
          <select
            className="aptean-input"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value as MethodFilter)}
            style={{ width: 120 }}
          >
            <option value="all">All</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="slack">Slack</option>
            <option value="msteams">MS Teams</option>
            <option value="webhook">Webhook</option>
          </select>
        </label>
      </div>

      {/* Subscriber list */}
      {loading ? (
        <div className="ac-empty">
          <div className="ac-empty__title">Loading…</div>
        </div>
      ) : subscribers.length === 0 ? (
        <div className="ac-empty">
          <div className="ac-empty__title">No subscribers found</div>
          <div className="ac-empty__body">
            {search ? "Try a different search." : "Add subscribers to get started."}
          </div>
        </div>
      ) : (
        <div className="ac-subscriber-list">
          {subscribers.map((sub) => (
            <div key={sub.id} className="ac-subscriber-card">
              {/* Header row */}
              <div className="ac-subscriber-card__header">
                <span className="ac-subscriber-card__address">{displayAddress(sub)}</span>
                <div className="ac-subscriber-card__menu" ref={openMenu === sub.id ? menuRef : undefined}>
                  <button
                    type="button"
                    className="ac-kebab-btn"
                    onClick={() => setOpenMenu(openMenu === sub.id ? null : sub.id)}
                    aria-label="Subscriber actions"
                  >
                    ⋮
                  </button>
                  {openMenu === sub.id && (
                    <div className="ac-kebab-dropdown">
                      <button
                        type="button"
                        className="ac-kebab-dropdown__item ac-kebab-dropdown__item--danger"
                        onClick={() => handleDelete(sub.id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Detail row */}
              <div className="ac-subscriber-card__detail">
                <span className="ac-subscriber-card__contact">{displayAddress(sub)}</span>
                <MethodBadge method={sub.method} />
                {sub.notify_all ? (
                  <ServiceBadge label="All" />
                ) : (
                  sub.services.map((s) => (
                    <ServiceBadge key={s.id} label={`${s.service_name} ${s.region_name}`} />
                  ))
                )}
                <div className="ac-subscriber-card__menu-spacer">
                  <button
                    type="button"
                    className="ac-kebab-btn"
                    onClick={() => setOpenMenu(openMenu === `${sub.id}-detail` ? null : `${sub.id}-detail`)}
                    aria-label="Subscriber actions"
                  >
                    ⋮
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Column headers (above the cards) */}
      <style>{`
        .ac-subscriber-list {
          display: flex;
          flex-direction: column;
          gap: var(--aptean-space-4, 1rem);
          margin-top: var(--aptean-space-4, 1rem);
        }
        .ac-subscriber-card {
          border: 1px solid var(--aptean-border, #e2e4e9);
          border-radius: var(--aptean-radius-md, 8px);
          background: var(--aptean-surface-primary, #fff);
          overflow: hidden;
        }
        .ac-subscriber-card__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--aptean-space-3, 0.75rem) var(--aptean-space-4, 1rem);
          border-bottom: 1px solid var(--aptean-border, #e2e4e9);
        }
        .ac-subscriber-card__address {
          font-weight: 500;
          color: var(--aptean-text-primary, #1a1a2e);
          font-size: 0.9rem;
        }
        .ac-subscriber-card__detail {
          display: flex;
          align-items: center;
          gap: var(--aptean-space-3, 0.75rem);
          padding: var(--aptean-space-3, 0.75rem) var(--aptean-space-4, 1rem);
        }
        .ac-subscriber-card__contact {
          min-width: 240px;
          color: var(--aptean-text-secondary, #64748b);
          font-size: 0.85rem;
        }
        .ac-subscriber-card__menu {
          position: relative;
        }
        .ac-subscriber-card__menu-spacer {
          margin-left: auto;
          position: relative;
        }
        .ac-badge--method {
          background: var(--aptean-surface-secondary, #f1f5f9);
          color: var(--aptean-text-primary, #1a1a2e);
          padding: 0.15rem 0.6rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .ac-badge--service {
          background: var(--aptean-surface-secondary, #f1f5f9);
          color: var(--aptean-text-secondary, #64748b);
          padding: 0.15rem 0.6rem;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        .ac-kebab-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.2rem;
          color: var(--aptean-text-secondary, #64748b);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          line-height: 1;
        }
        .ac-kebab-btn:hover {
          background: var(--aptean-surface-secondary, #f1f5f9);
        }
        .ac-kebab-dropdown {
          position: absolute;
          right: 0;
          top: 100%;
          background: var(--aptean-surface-primary, #fff);
          border: 1px solid var(--aptean-border, #e2e4e9);
          border-radius: var(--aptean-radius-md, 8px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
          z-index: 100;
          min-width: 120px;
          padding: 0.25rem 0;
        }
        .ac-kebab-dropdown__item {
          display: block;
          width: 100%;
          text-align: left;
          padding: 0.5rem 1rem;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 0.85rem;
          color: var(--aptean-text-primary, #1a1a2e);
        }
        .ac-kebab-dropdown__item:hover {
          background: var(--aptean-surface-secondary, #f1f5f9);
        }
        .ac-kebab-dropdown__item--danger {
          color: #dc2626;
        }
        .ac-filter-label {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--aptean-text-secondary, #64748b);
        }
        .ac-subscriber-col-headers {
          display: none; /* Labels in the screenshot are above the table area */
        }
      `}</style>

      {/* Add subscriber dialog */}
      {showCreate && (
        <div className="ac-overlay" onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Add Subscriber</h2>
              <button
                type="button"
                className="ac-dialog__close"
                onClick={() => setShowCreate(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error">{error}</div>}
                <div className="ac-field">
                  <label htmlFor="sub-method">Method</label>
                  <select
                    id="sub-method"
                    className="aptean-input"
                    value={fMethod}
                    onChange={(e) => setFMethod(e.target.value)}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="slack">Slack</option>
                    <option value="msteams">MS Teams</option>
                    <option value="webhook">Webhook</option>
                  </select>
                </div>
                <div className="ac-field">
                  <label htmlFor="sub-contact">
                    {fMethod === "email"
                      ? "Email address"
                      : fMethod === "phone"
                        ? "Phone number"
                        : fMethod === "slack"
                          ? "Slack webhook URL"
                          : fMethod === "msteams"
                            ? "Teams webhook URL"
                            : "Webhook URL"}
                  </label>
                  <input
                    id="sub-contact"
                    type="text"
                    className="aptean-input"
                    value={fContact}
                    onChange={(e) => setFContact(e.target.value)}
                    required
                    placeholder={fMethod === "email" ? "user@example.com" : ""}
                  />
                </div>
                <div className="ac-field">
                  <label className="subscribe-checkbox">
                    <input
                      type="checkbox"
                      checked={fNotifyAll}
                      onChange={(e) => setFNotifyAll(e.target.checked)}
                    />
                    Subscribe to all services
                  </label>
                </div>
                {!fNotifyAll && (
                  <div className="ac-field">
                    <label>Select services</label>
                    <div
                      style={{
                        maxHeight: 200,
                        overflow: "auto",
                        border: "1px solid var(--aptean-border, #e2e4e9)",
                        borderRadius: "var(--aptean-radius-md, 8px)",
                        padding: "0.5rem",
                      }}
                    >
                      {(() => {
                        // Build hierarchy: Service → Region → Components
                        const tree: Record<string, Record<string, ServiceRegionOption[]>> = {};
                        for (const sr of serviceRegions) {
                          if (!tree[sr.service_name]) tree[sr.service_name] = {};
                          if (!tree[sr.service_name][sr.region_name]) tree[sr.service_name][sr.region_name] = [];
                          tree[sr.service_name][sr.region_name].push(sr);
                        }

                        return Object.entries(tree).map(([serviceName, regions]) => {
                          const serviceIds = Object.values(regions).flat().map((sr) => sr.id);
                          const allServiceChecked = serviceIds.every((id) => fServiceRegionIds.includes(id));
                          const someServiceChecked = serviceIds.some((id) => fServiceRegionIds.includes(id));

                          return (
                            <div key={serviceName} style={{ marginBottom: "0.5rem" }}>
                              {/* Service level */}
                              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600, cursor: "pointer" }}>
                                <input
                                  type="checkbox"
                                  checked={allServiceChecked}
                                  ref={(el) => { if (el) el.indeterminate = someServiceChecked && !allServiceChecked; }}
                                  onChange={() => {
                                    if (allServiceChecked) {
                                      setFServiceRegionIds((prev) => prev.filter((id) => !serviceIds.includes(id)));
                                    } else {
                                      setFServiceRegionIds((prev) => [...new Set([...prev, ...serviceIds])]);
                                    }
                                  }}
                                />
                                {serviceName}
                              </label>

                              {Object.entries(regions).map(([regionName, items]) => {
                                const regionIds = items.map((sr) => sr.id);
                                const allRegionChecked = regionIds.every((id) => fServiceRegionIds.includes(id));
                                const someRegionChecked = regionIds.some((id) => fServiceRegionIds.includes(id));
                                // Show components if there are multiple items OR any item has component_name
                                const showChildren = items.length > 1 || items.some((sr) => sr.component_name);

                                return (
                                  <div key={regionName} style={{ paddingLeft: "1.25rem" }}>
                                    {/* Region level */}
                                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.15rem 0", cursor: "pointer", fontWeight: showChildren ? 500 : 400 }}>
                                      <input
                                        type="checkbox"
                                        checked={allRegionChecked}
                                        ref={(el) => { if (el) el.indeterminate = someRegionChecked && !allRegionChecked; }}
                                        onChange={() => {
                                          if (allRegionChecked) {
                                            setFServiceRegionIds((prev) => prev.filter((id) => !regionIds.includes(id)));
                                          } else {
                                            setFServiceRegionIds((prev) => [...new Set([...prev, ...regionIds])]);
                                          }
                                        }}
                                      />
                                      {regionName}
                                    </label>

                                    {/* Component level — show individual items when there are multiple or component_name exists */}
                                    {showChildren && items.map((sr) => (
                                      <label
                                        key={sr.id}
                                        style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.15rem 0 0.15rem 1.25rem", cursor: "pointer" }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={fServiceRegionIds.includes(sr.id)}
                                          onChange={() => toggleServiceRegion(sr.id)}
                                        />
                                        {sr.component_name || `${serviceName} (${regionName})`}
                                      </label>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
              <div className="ac-dialog__footer">
                <button
                  type="button"
                  className="aptean-btn aptean-btn--ghost"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aptean-btn aptean-btn--primary"
                  disabled={submitting}
                >
                  {submitting ? "Saving…" : "Add subscriber"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
